// Cross-Machine Correlation Engine
// Detects whether alerts are fleet-wide (environmental) or machine-specific (local fault)

export type CorrelationType = 'ENVIRONMENTAL' | 'MACHINE_SPECIFIC' | 'CASCADING';

export interface CorrelationResult {
  type:             CorrelationType;
  affectedMachines: string[];           // machine names
  sharedStage:      string;             // the stage they share
  sharedPatterns:   string[];           // pattern names seen on 2+ machines
  confidence:       number;             // 0–100
  insight:          string;             // human-readable explanation
  action:           string;             // recommended action
}

interface MachineSummary {
  machineId:   string;
  machineName: string;
  stage:       string;
  avgSlope:    number;
  patterns:    string[];
  healthScore: number;
}

const MIN_MACHINES_FOR_CORRELATION = 2;
// If ≥ this fraction of machines share the same stage → environmental
const ENVIRONMENTAL_THRESHOLD = 0.6;

export function correlate(machines: MachineSummary[]): CorrelationResult | null {
  // Only count MID or LATE as real alerts — EARLY alone is too noisy
  const alerting = machines.filter(m => m.stage === 'MID' || m.stage === 'LATE');

  if (alerting.length < MIN_MACHINES_FOR_CORRELATION) return null;

  const total    = machines.length;
  const fraction = alerting.length / total;

  // ── Count stage distribution ───────────────────────────────
  const stageCounts: Record<string, number> = {};
  alerting.forEach(m => { stageCounts[m.stage] = (stageCounts[m.stage] || 0) + 1; });
  const dominantStage = Object.entries(stageCounts).sort((a, b) => b[1] - a[1])[0][0];

  // ── Find shared failure patterns ───────────────────────────
  const patternCounts: Record<string, number> = {};
  alerting.forEach(m => m.patterns.forEach(p => {
    patternCounts[p] = (patternCounts[p] || 0) + 1;
  }));
  const sharedPatterns = Object.entries(patternCounts)
    .filter(([, count]) => count >= MIN_MACHINES_FOR_CORRELATION)
    .map(([name]) => name);

  // ── Detect cascading: machines degrading in sequence ──────
  // Cascading = machines at different stages but all trending same direction
  const hasEarly = alerting.some(m => m.stage === 'EARLY');
  const hasMid   = alerting.some(m => m.stage === 'MID');
  const hasLate  = alerting.some(m => m.stage === 'LATE');
  const isCascading = hasEarly && (hasMid || hasLate) && alerting.length >= 2;

  // ── Determine correlation type ─────────────────────────────
  let type:       CorrelationType;
  let insight:    string;
  let action:     string;
  let confidence: number;

  if (fraction >= ENVIRONMENTAL_THRESHOLD && sharedPatterns.length > 0) {
    type       = 'ENVIRONMENTAL';
    confidence = Math.round(Math.min(fraction * 100 + sharedPatterns.length * 10, 99));
    insight    = `${alerting.length}/${total} machines showing ${dominantStage} alerts with shared pattern "${sharedPatterns[0]}". This is likely an environmental or systemic issue, not individual machine faults.`;
    action     = 'Check shared infrastructure: power supply, cooling system, ambient temperature, vibration from building. Do not service machines individually until root cause is found.';
  } else if (isCascading) {
    type       = 'CASCADING';
    confidence = Math.round(Math.min(alerting.length * 20 + 30, 99));
    insight    = `Machines are failing in sequence (${alerting.map(m => `${m.machineName}: ${m.stage}`).join(', ')}). A failure in one machine may be stressing downstream machines.`;
    action     = 'Inspect the machine in LATE stage first. Reducing its load may relieve stress on others. Check mechanical coupling between machines.';
  } else {
    type       = 'MACHINE_SPECIFIC';
    confidence = Math.round(Math.min((1 - fraction) * 100, 99));
    insight    = `${alerting.length} machine${alerting.length > 1 ? 's' : ''} showing isolated faults (${alerting.map(m => m.machineName).join(', ')}). Other machines are healthy — this is a machine-specific issue.`;
    action     = 'Service the affected machine(s) individually. No systemic issue detected.';
  }

  return {
    type,
    affectedMachines: alerting.map(m => m.machineName),
    sharedStage:      dominantStage,
    sharedPatterns,
    confidence,
    insight,
    action,
  };
}
